import { useState } from 'react';
import { useAuth } from '../firebase/AuthContext';
import { trackButtonClick } from '../services/analytics';

const AuthButtons = ({ variant = 'primary', testResults = null }) => {
  const { user, signInDirect, signUpWithDetails } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleSignIn = async () => {
    setLoading(true);
    trackButtonClick('google_sign_in_direct', variant);
    await signInDirect(testResults);
    setLoading(false);
  };

  const handleSignUp = async () => {
    setLoading(true);
    trackButtonClick('google_sign_up', variant);
    await signUpWithDetails(testResults);
    setLoading(false);
  };

  // If user is already logged in, show their info
  if (user) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 border border-gray-200">
        {user.photoURL && (
          <img 
            src={user.photoURL} 
            alt={user.displayName}
            className="w-7 h-7 rounded-full"
          />
        )}
        <span className="text-sm font-medium text-gray-700">
          {user.displayName || user.email}
        </span>
      </div>
    );
  }

  // Primary variant - side by side buttons with OR
  if (variant === 'primary') {
    return (
      <div className="flex flex-col sm:flex-row items-center gap-3 w-full max-w-md mx-auto">
        <button
          onClick={handleSignIn}
          disabled={loading}
          className="flex-1 w-full flex items-center justify-center gap-3 px-6 py-3 text-base font-medium text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711L.957 13.239C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
            <path d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707L.957 4.761A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          <span>{loading ? 'Signing in...' : 'Sign In'}</span>
        </button>

        <span className="text-gray-500 font-medium text-sm">OR</span>

        <button
          onClick={handleSignUp}
          disabled={loading}
          className="flex-1 w-full flex items-center justify-center gap-3 px-6 py-3 text-base font-medium text-white bg-primary border-2 border-primary rounded-lg hover:bg-primary-dark transition-all disabled:opacity-60 disabled:cursor-not-allowed shadow-md"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#FFFFFF"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711L.957 13.239C2.438 15.983 5.482 18 9 18z" fill="#FFFFFF"/>
            <path d="M3.964 10.707A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.707L.957 4.761A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FFFFFF"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#FFFFFF"/>
          </svg>
          <span className="whitespace-nowrap">{loading ? 'Signing up...' : 'Sign Up'}</span>
        </button>
      </div>
    );
  }

  // Header variant - compact for navbar
  if (variant === 'header') {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={handleSignIn}
          disabled={loading}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-60"
        >
          Sign In
        </button>
        <span className="text-gray-400 text-xs">or</span>
        <button
          onClick={handleSignUp}
          disabled={loading}
          className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-all disabled:opacity-60 whitespace-nowrap"
        >
          Sign Up
        </button>
      </div>
    );
  }

  return null;
};

export default AuthButtons;